/**
 * The presenter state contract every adapter consumes: read `state`, react in
 * `subscribe`, call commands. AngularJS wraps the listener in `$evalAsync`;
 * Angular copies it into a signal.
 */

export type Listener<S> = (state: S) => void;

export interface Observable<S> {
  readonly state: S;
  /** Calls the listener now and on every change; returns the unsubscribe. */
  subscribe(listener: Listener<S>): () => void;
}

export interface StateBox<S> extends Observable<S> {
  set(patch: Partial<S>): void;
}

export function createStateBox<S extends object>(initial: S): StateBox<S> {
  let state = initial;
  const listeners = new Set<Listener<S>>();
  return {
    get state() {
      return state;
    },
    set(patch) {
      state = { ...state, ...patch };
      listeners.forEach((listener) => listener(state));
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
